/**
 * CustomPDFViewer - Interactive PDF viewer with block extraction
 * Features:
 * - Click blocks to inspect JSON
 * - Export tables to Excel
 * - Copy images/text
 * - Highlight specific blocks from RAG citations
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, TableChart, Image as ImageIcon, ArrowBack } from '@mui/icons-material';
import { supabase } from '../services/supabaseClient';
import { exportTableToExcel } from '../utils/excelExport';

interface Block {
  id: string;
  block_type: string;
  bbox: [number, number, number, number];
  text?: string;
  html?: string;
  figure_caption?: string;
  children?: Block[];
  images?: Record<string, string>;
}

interface Document {
  id: string;
  title: string;
  storage_url: string;
  layout_data: Block[];
}

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

export default function CustomPDFViewer() {
  const [searchParams] = useSearchParams();
  const docId = searchParams.get('id');
  const highlightBlockId = searchParams.get('block');
  const highlightPage = searchParams.get('page');

  const [document, setDocument] = useState<Document | null>(null);
  const [blocks, setBlocks] = useState<Record<string, Block>>({});
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setPdfLoaded] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarBlock, setToolbarBlock] = useState<Block | null>(null);
  const [toolbarPos, setToolbarPos] = useState({ top: 0, left: 0 });

  // Load PDF.js
  useEffect(() => {
    if (!window.pdfjsLib) {
      const script = window.document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      };
      window.document.body.appendChild(script);
    }
  }, []);

  // Fetch document
  useEffect(() => {
    if (!docId) return;
    
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('documents')
          .select('id, title, storage_url, layout_data')
          .eq('id', docId)
          .single();
        
        if (error) throw error;
        if (!data) throw new Error('Document not found');
        
        setDocument(data);
        
        // Flatten blocks
        const flatBlocks = flattenBlocks(data.layout_data);
        setBlocks(flatBlocks);
        
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // Render PDF when document loaded
  useEffect(() => {
    if (!document?.storage_url || !window.pdfjsLib || !containerRef.current) return;
    
    const renderPDF = async () => {
      try {
        const pdf = await window.pdfjsLib.getDocument(document.storage_url).promise;
        const container = containerRef.current;
        if (!container) return;
        
        container.innerHTML = '';
        
        for (let i = 1; i <= pdf.numPages; i++) {
          await renderPage(pdf, i, container);
        }
        
        setPdfLoaded(true);
        
        // Scroll to highlighted block after render
        if (highlightBlockId) {
          setTimeout(() => {
            const el = window.document.getElementById(highlightBlockId);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('highlight-pulse');
            }
          }, 500);
        } else if (highlightPage) {
          setTimeout(() => {
            const pageEl = container.querySelector(`[data-page="${highlightPage}"]`);
            if (pageEl) {
              pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 500);
        }
        
      } catch (err: any) {
        setError(`PDF load error: ${err.message}`);
      }
    };
    
    renderPDF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, blocks, highlightBlockId, highlightPage]);

  // Normalize bbox to array format [x, y, x2, y2]
  const normalizeBbox = (bbox: any): [number, number, number, number] | null => {
    if (!bbox) return null;
    if (Array.isArray(bbox) && bbox.length >= 4) {
      return [bbox[0], bbox[1], bbox[2], bbox[3]];
    }
    if (typeof bbox === 'object' && 'x' in bbox && 'y' in bbox) {
      const x = bbox.x || 0;
      const y = bbox.y || 0;
      const width = bbox.width || 0;
      const height = bbox.height || 0;
      return [x, y, x + width, y + height];
    }
    return null;
  };

  // Normalize block_type to TitleCase (e.g., 'text' -> 'Text')
  const normalizeBlockType = (blockType: string): string => {
    if (!blockType) return blockType;
    return blockType.charAt(0).toUpperCase() + blockType.slice(1).toLowerCase();
  };

  // Process a single block into the map
  const processBlockToMap = (node: any, map: Record<string, Block>): void => {
    if (!node || typeof node !== 'object') return;
    const blockId = node.block_id || node.id;
    const blockType = normalizeBlockType(node.block_type);
    if (blockId && node.bbox && blockType !== 'Page') {
      const normalizedBbox = normalizeBbox(node.bbox);
      if (normalizedBbox) {
        let pageNum = node.page_number ?? node.page;
        if (pageNum === undefined && typeof node.bbox === 'object' && 'page' in node.bbox) {
          pageNum = node.bbox.page;
        }
        if (pageNum === undefined) {
          const pageMatch = blockId.match(/\/page\/(\d+)/);
          if (pageMatch) pageNum = parseInt(pageMatch[1], 10);
        }
        map[blockId] = { ...node, id: blockId, block_type: blockType, bbox: normalizedBbox, page: pageNum };
      }
    }
  };

  const flattenBlocks = (layoutData: any, map: Record<string, Block> = {}): Record<string, Block> => {
    if (!layoutData) return map;
    if (Array.isArray(layoutData)) {
      layoutData.forEach(n => flattenBlocks(n, map));
      return map;
    }
    // Handle new format with 'blocks' array
    if (typeof layoutData === 'object' && 'blocks' in layoutData && Array.isArray(layoutData.blocks)) {
      layoutData.blocks.forEach((block: any) => processBlockToMap(block, map));
    }
    // Also traverse nested structure
    if (typeof layoutData === 'object') {
      processBlockToMap(layoutData, map);
      if (layoutData.children) {
        flattenBlocks(layoutData.children, map);
      }
      if (layoutData.pages) {
        if (Array.isArray(layoutData.pages)) {
          layoutData.pages.forEach((page: any) => flattenBlocks(page, map));
        } else if (typeof layoutData.pages === 'object') {
          Object.values(layoutData.pages).forEach((page: any) => flattenBlocks(page, map));
        }
      }
    }
    return map;
  };

  const renderPage = async (pdf: any, pageNum: number, container: HTMLDivElement) => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 });
    const baseViewport = page.getViewport({ scale: 1.0 });
    const scaleX = viewport.width / baseViewport.width;
    const scaleY = viewport.height / baseViewport.height;

    const pageDiv = window.document.createElement('div');
    pageDiv.className = 'pdf-page';
    pageDiv.dataset.page = String(pageNum);
    pageDiv.style.cssText = `
      position: relative;
      margin: 0 auto 30px;
      box-shadow: 0 8px 20px rgba(0,0,0,0.4);
      background: white;
      width: ${viewport.width}px;
      height: ${viewport.height}px;
    `;

    const canvas = window.document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    pageDiv.appendChild(canvas);

    const layer = window.document.createElement('div');
    layer.className = 'chunk-layer';
    layer.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none;';

    Object.values(blocks).forEach(block => {
      // Block IDs are 0-indexed from backend (e.g., /page/0/Text/5)
      const pageIdx = parseInt(block.id.split('/')[2]);
      // pageNum is 1-indexed, block ID is 0-indexed, compare with pageNum - 1
      if (pageIdx !== pageNum - 1) return;
      
      // Skip parent blocks with children (except Tables)
      if (block.children?.length && block.block_type !== 'Table') return;
      
      // Skip TableRow/TableCell - we interact with Table parent only
      if (block.block_type === 'TableRow' || block.block_type === 'TableCell') return;

      const [x, y, x2, y2] = block.bbox;
      const el = window.document.createElement('div');
      el.className = `block ${block.id === highlightBlockId ? 'highlight-pulse' : ''}`;
      el.id = block.id;
      el.dataset.type = block.block_type;
      el.style.cssText = `
        position: absolute;
        pointer-events: auto;
        cursor: pointer;
        border: 1px solid transparent;
        transition: all 0.1s;
        left: ${x * scaleX}px;
        top: ${y * scaleY}px;
        width: ${(x2 - x) * scaleX}px;
        height: ${(y2 - y) * scaleY}px;
      `;

      el.onmouseenter = (e) => showToolbar(block, e.currentTarget as HTMLElement);
      el.onmouseleave = () => setTimeout(() => setToolbarBlock(null), 300);
      el.onclick = () => setSelectedBlock(block);

      layer.appendChild(el);
    });

    pageDiv.appendChild(layer);
    container.appendChild(pageDiv);
  };

  const showToolbar = (block: Block, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setToolbarPos({ top: rect.top - 40, left: rect.left });
    setToolbarBlock(block);
  };

  const copyText = (block: Block) => {
    const text = block.figure_caption || (block.html || block.text || '').replace(/<[^>]+>/g, '');
    navigator.clipboard.writeText(text);
  };

  const copyImage = async (block: Block) => {
    const imageData = block.images?.[block.id];
    if (!imageData) {
      alert('No image data available');
      return;
    }
    
    try {
      const response = await fetch(`data:image/png;base64,${imageData}`);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } catch (err) {
      alert('Failed to copy image');
    }
  };

  const exportExcel = (block: Block) => {
    const parts = block.id.split('/');
    const filename = `Page${+parts[2] + 1}_Table${+parts[4] + 1}.xlsx`;
    exportTableToExcel(block, filename);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#1e1e1e' }}>
      {/* Sidebar - Block Inspector */}
      <Paper sx={{ 
        width: 350, 
        display: 'flex', 
        flexDirection: 'column',
        borderRight: '1px solid #ddd'
      }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
          <IconButton size="small" onClick={() => window.history.back()} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1" component="span" fontWeight={600}>
            Block Inspector
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {document?.title}
          </Typography>
          <Box sx={{ 
            bgcolor: '#2d2d2d', 
            color: '#76ff03', 
            p: 1.5, 
            borderRadius: 1,
            fontFamily: 'Consolas, monospace',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            minHeight: 150,
            overflow: 'auto'
          }}>
            {selectedBlock 
              ? JSON.stringify(selectedBlock, null, 2) 
              : 'Click on a block to inspect...'}
          </Box>
        </Box>
      </Paper>

      {/* PDF Viewer */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#525659' }}>
        <div ref={containerRef} />
      </Box>

      {/* Floating Toolbar */}
      {toolbarBlock && (
        <Paper
          ref={toolbarRef}
          sx={{
            position: 'fixed',
            top: toolbarPos.top,
            left: toolbarPos.left,
            bgcolor: '#333',
            color: '#fff',
            p: 0.5,
            borderRadius: 1,
            zIndex: 9999,
            display: 'flex',
            gap: 0.5
          }}
          onMouseEnter={() => setToolbarBlock(toolbarBlock)}
          onMouseLeave={() => setToolbarBlock(null)}
        >
          {toolbarBlock.block_type === 'Table' ? (
            <Tooltip title="Export to Excel">
              <IconButton size="small" onClick={() => exportExcel(toolbarBlock)} sx={{ color: '#4caf50' }}>
                <TableChart fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (toolbarBlock.block_type === 'Figure' || toolbarBlock.block_type === 'Picture') ? (
            <Tooltip title="Copy Image">
              <IconButton size="small" onClick={() => copyImage(toolbarBlock)} sx={{ color: '#fff' }}>
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Copy Text">
              <IconButton size="small" onClick={() => copyText(toolbarBlock)} sx={{ color: '#fff' }}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
      )}

      {/* Styles */}
      <style>{`
        .pdf-page .block:hover {
          background: rgba(0, 102, 204, 0.1);
          border: 1px solid #0066cc !important;
        }
        .pdf-page .block.active {
          background: rgba(255, 235, 59, 0.3);
          border: 2px solid #fdd835 !important;
        }
        .pdf-page .block[data-type="Table"] {
          border: 1px dashed rgba(255, 152, 0, 0.4);
        }
        .pdf-page .block[data-type="Table"]:hover {
          background: rgba(255, 152, 0, 0.15);
          border: 2px solid #ff9800 !important;
        }
        .pdf-page .block[data-type="Figure"],
        .pdf-page .block[data-type="Picture"] {
          border: 1px dashed rgba(156, 39, 176, 0.4);
        }
        .pdf-page .block[data-type="Figure"]:hover,
        .pdf-page .block[data-type="Picture"]:hover {
          background: rgba(156, 39, 176, 0.15);
          border: 2px solid #9c27b0 !important;
        }
        .highlight-pulse {
          animation: pulse 2s ease-in-out 3;
        }
        @keyframes pulse {
          0%, 100% { background: rgba(255, 235, 59, 0.1); }
          50% { background: rgba(255, 235, 59, 0.5); border: 3px solid #fdd835 !important; }
        }
      `}</style>
    </Box>
  );
}
